import { PRODUCTS_ACTION_TYPES } from './actionTypes';
import {
    normalizeItems,
    mergeNormalizedData,
    updateIds,
} from "../utility";

const initialState = {
    byId: {},
    allIds: [],
    items: [],

    search: {
        items: [],
        loading: false,
        error: null,
    },

    loading: {
        list: false,
        items: {},
        categories: {},
    },

    error: {
        list: null,
        items: {},
        categories: {},
    },

    categoriesMap: {},

    categoriesLoaded: {},

    pagination: {
        currentPage: 1,
        totalPages: 0,
        total: 0,
        hasMore: true,
    },

    filters: {
        search: '',
        categoryId: null,
        orderBy: null,
        order: 'desc',
    },

    related: {},  // { [productId]: { items: [], loading: false, error: null } }

    lastUpdated: null,
};

export const productsReducer = (state = initialState, action) => {
    switch (action.type) {
        case PRODUCTS_ACTION_TYPES.FETCH_LIST_REQUEST: {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    list: true,
                },
                error: {
                    ...state.error,
                    list: null,
                }
            };
        }
        case PRODUCTS_ACTION_TYPES.FETCH_LIST_SUCCESS: {
            const { data, page, total, totalPages, hasMore } = action.payload;
            const { byId: newById, allIds: newIds } = normalizeItems(data);

            const isFirstPage = page === 1;

            return {
                ...state,
                byId: isFirstPage
                    ? newById
                    : mergeNormalizedData(state.byId, newById),
                allIds: updateIds(state.allIds, newIds, isFirstPage),
                items: isFirstPage
                    ? data
                    : [...state.items, ...data],
                loading: {
                    ...state.loading,
                    list: false,
                },
                error: {
                    ...state.error,
                    list: null,
                },
                pagination: {
                    currentPage: page,
                    totalPages,
                    total,
                    hasMore,
                },
                lastUpdated: action.meta.timestamp,
            };
        }
        case PRODUCTS_ACTION_TYPES.FETCH_LIST_FAILURE: {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    list: false,
                },
                error: {
                    ...state.error,
                    list: action.payload.error,
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_REQUEST: {
            const { categoryId, refresh } = action.payload;
            return {
                ...state,
                categoriesMap: refresh
                    ? { ...state.categoriesMap, [categoryId]: [] }
                    : state.categoriesMap,
                categoriesLoaded: refresh
                    ? { ...state.categoriesLoaded, [categoryId]: undefined }
                    : state.categoriesLoaded,
                loading: {
                    ...state.loading,
                    categories: {
                        ...state.loading.categories,
                        [categoryId]: true,
                    },
                },
                error: {
                    ...state.error,
                    categories: {
                        ...state.error.categories,
                        [categoryId]: null,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_SUCCESS: {
            const {
                categoryId,
                data,
                total,
                totalPages,
                currentPage,
                hasMore,
                perPage,
            } = action.payload;

            const { byId: newById, allIds: newIds } = normalizeItems(data);

            const isFirstPage = currentPage === 1;

            const existingCategoryIds = state.categoriesMap[categoryId] || [];

            let updatedCategoryIds;
            if (isFirstPage) {
                updatedCategoryIds = newIds;
            } else {
                const uniqueNewIds = newIds.filter(
                    id => !existingCategoryIds.includes(id)
                );
                updatedCategoryIds = [...existingCategoryIds, ...uniqueNewIds];
            }

            return {
                ...state,
                byId: mergeNormalizedData(state.byId, newById),
                allIds: updateIds(state.allIds, newIds, false),
                items: [...state.items, ...data],

                categoriesMap: {
                    ...state.categoriesMap,
                    [categoryId]: updatedCategoryIds,
                },

                categoriesLoaded: {
                    ...state.categoriesLoaded,
                    [categoryId]: {
                        loaded: true,
                        timestamp: action.meta.timestamp,
                        total,
                        currentPage,
                        totalPages,
                        hasMore,
                        perPage,
                    },
                },

                loading: {
                    ...state.loading,
                    categories: {
                        ...state.loading.categories,
                        [categoryId]: false,
                    },
                },

                error: {
                    ...state.error,
                    categories: {
                        ...state.error.categories,
                        [categoryId]: null,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_FAILURE: {
            const { categoryId, error } = action.payload;
            return {
                ...state,
                loading: {
                    ...state.loading,
                    categories: {
                        ...state.loading.categories,
                        [categoryId]: false,
                    },
                },
                error: {
                    ...state.error,
                    categories: {
                        ...state.error.categories,
                        [categoryId]: error,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.SEARCH_REQUEST: {
            return {
                ...state,
                search: {
                    ...state.search,
                    loading: true,
                    error: null,
                }
            };
        }

        case PRODUCTS_ACTION_TYPES.SEARCH_SUCCESS: {
            return {
                ...state,
                search: {
                    items: action.payload.data,
                    loading: false,
                    error: null,
                }
            };
        }

        case PRODUCTS_ACTION_TYPES.SEARCH_FAILURE: {
            return {
                ...state,
                search: {
                    ...state.search,
                    loading: false,
                    error: action.payload.error,
                }
            };
        }
        case PRODUCTS_ACTION_TYPES.CLEAR_SEARCH: {
            return {
                ...state,
                search: {
                    items: [],
                    loading: false,
                    error: null,
                }
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_ITEM_REQUEST: {
            const { id } = action.payload;
            return {
                ...state,
                loading: {
                    ...state.loading,
                    items: {
                        ...state.loading.items,
                        [id]: true,
                    },
                },
                error: {
                    ...state.error,
                    items: {
                        ...state.error.items,
                        [id]: null,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_ITEM_SUCCESS: {
            const { data, id } = action.payload;

            const newById = { [id]: data };

            const idExists = state.allIds.includes(id);
            const newAllIds = idExists ? state.allIds : [...state.allIds, data.id];

            let newItems = [...state.items];
            if (idExists) {
                newItems = newItems.map(item => item.id === data.id ? data : item);
            } else {
                newItems.push(data);
            }

            return {
                ...state,
                byId: {
                    ...state.byId,
                    ...newById
                },
                allIds: newAllIds,
                items: newItems,
                loading: {
                    ...state.loading,
                    items: {
                        ...state.loading.items,
                        [id]: false,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_ITEM_FAILURE: {
            const { id, error } = action.payload;
            return {
                ...state,
                loading: {
                    ...state.loading,
                    items: {
                        ...state.loading.items,
                        [id]: false,
                    },
                },
                error: {
                    ...state.error,
                    items: {
                        ...state.error.items,
                        [id]: error,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_RELATED_REQUEST: {
            const { productId } = action.payload;
            return {
                ...state,
                related: {
                    ...state.related,
                    [productId]: {
                        items: state.related[productId]?.items || [],
                        loading: true,
                        error: null,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_RELATED_SUCCESS: {
            const { productId, data } = action.payload;
            const { byId: newById, allIds: newIds } = normalizeItems(data);

            return {
                ...state,
                byId: mergeNormalizedData(state.byId, newById),
                allIds: updateIds(state.allIds, newIds, false),
                related: {
                    ...state.related,
                    [productId]: {
                        items: data.map(p => p.id),
                        loading: false,
                        error: null,
                    },
                },
            };
        }

        case PRODUCTS_ACTION_TYPES.FETCH_RELATED_FAILURE: {
            const { productId, error } = action.payload;
            return {
                ...state,
                related: {
                    ...state.related,
                    [productId]: {
                        items: [],
                        loading: false,
                        error,
                    },
                },
            };
        }

        default:
            return state;
    }
}